
import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import { useSidebar } from "../hooks";
import { API_BASE_URL } from "../config";
import { Session } from "./utils/session";
import "./css/Settings.css";

const authHeaders = () => {
  const token = Session.getToken();
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

function getKey(item) {
  return (item.SKey ?? item.skey ?? item.sKey ?? "").toString();
}

function getValue(item) {
  const v = item.SValue ?? item.svalue ?? item.sValue;
  return v == null ? "" : String(v);
}

function getSid(item) {
  return item.Sid ?? item.sid ?? null;
}

export default function SuperSetting() {
  const { toggleSidebar } = useSidebar();
  const [rows, setRows] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingSid, setSavingSid] = useState(null);
  const [statusMsg, setStatusMsg] = useState("");
  const [statusType, setStatusType] = useState("");
  const timerRef = useRef(null);

  const isAdmin = Session.isAdmin();

  function showStatus(msg, type) {
    setStatusMsg(msg);
    setStatusType(type);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setStatusMsg("");
      setStatusType("");
    }, 2500);
  }

  async function loadSettings() {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Setting/All_Setting`, {
        method: "GET",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Failed to load settings");
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setRows(list);
      const map = {};
      list.forEach((item) => {
        const sid = getSid(item);
        if (sid != null) map[sid] = getValue(item);
      });
      setDrafts(map);
    } catch {
      showStatus("Failed to load settings.", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    loadSettings();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  async function saveRow(item) {
    const sid = getSid(item);
    if (sid == null) {
      showStatus("Missing setting id.", "error");
      return;
    }
    const value = drafts[sid] ?? "";
    setSavingSid(sid);
    try {
      const res = await fetch(`${API_BASE_URL}/api/Setting/Update_Setting_Value`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ Sid: sid, SValue: value }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) {
        const msg =
          (body && (body.Message || body.message)) ||
          "Failed to update setting.";
        showStatus(msg, "error");
        return;
      }
      showStatus(
        (body && (body.Message || body.message)) || "Setting updated.",
        "success"
      );
      await loadSettings();
    } catch {
      showStatus("Failed to update setting.", "error");
    } finally {
      setSavingSid(null);
    }
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div>
      <div id="sidebar-overlay" />
      <Sidebar />
      <div id="content">
        <div className="d-flex align-items-center mb-3">
          <button
            type="button"
            id="sidebarCollapse"
            className="btn btn-toggle me-3"
            onClick={toggleSidebar}
          >
            <i className="fas fa-bars" />
          </button>
        </div>

        <div className="product-page-wrapper">
          <div className="main-card">
            <div className="panel panel-default">
              <div className="panel-body p-0">
                <div className="mb-0 p-4">
                  <div className="d-flex justify-content-end mb-2">
                    <div className={`toast-container ${statusMsg ? "show" : ""}`}>
                      {statusMsg && (
                        <div className={`toast-message ${statusType || "info"}`}>
                          {statusMsg}
                          <button
                            className="toast-close"
                            onClick={() => {
                              setStatusMsg("");
                              setStatusType("");
                            }}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <h4 className="mb-1">Super Setting</h4>
                  <p className="text-muted mb-4" style={{ fontSize: 13 }}>
                    Admin only. Update values from the Setting table (e.g.{" "}
                    <code>allowactivemembers</code>,{" "}
                    <code>AppUserUnablePassword</code>).
                  </p>

                  {loading ? (
                    <p className="text-muted">Loading…</p>
                  ) : rows.length === 0 ? (
                    <p className="text-muted">No settings found.</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead>
                          <tr>
                            <th style={{ width: "28%" }}>Key</th>
                            <th>Value</th>
                            <th style={{ width: 110 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((item) => {
                            const sid = getSid(item);
                            const key = getKey(item);
                            const original = getValue(item);
                            const draft = sid != null ? drafts[sid] ?? "" : "";
                            const dirty = draft !== original;
                            return (
                              <tr key={sid ?? key}>
                                <td>
                                  <code>{key || "—"}</code>
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={draft}
                                    onChange={(e) =>
                                      setDrafts((prev) => ({
                                        ...prev,
                                        [sid]: e.target.value,
                                      }))
                                    }
                                  />
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    disabled={!dirty || savingSid === sid}
                                    onClick={() => saveRow(item)}
                                  >
                                    {savingSid === sid ? "Saving…" : "Update"}
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
